package ai.jessie.web.work

import ai.jessie.shared.work.WorkCalendarDay
import ai.jessie.shared.work.WorkCalendarResponse
import ai.jessie.shared.work.WorkResponse
import ai.jessie.shared.work.WorkSelectionsResponse
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CalendarAllDayStyle
import ai.jessie.web.theme.CalendarDayControlStyle
import ai.jessie.web.theme.CalendarEventStyle
import ai.jessie.web.theme.CalendarMonthStyle
import ai.jessie.web.theme.CalendarMonthTitleStyle
import ai.jessie.web.theme.CalendarScrollStyle
import ai.jessie.web.theme.CalendarSumStyle
import ai.jessie.web.theme.CalendarTableRowStyle
import ai.jessie.web.theme.CalendarTableStyle
import ai.jessie.web.theme.CalendarWeekDayStyle
import ai.jessie.web.theme.CalendarWeekHeadStyle
import ai.jessie.web.theme.CalendarWeekStyle
import ai.jessie.web.theme.CardHeadRowStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.DayStyle
import ai.jessie.web.theme.HeadTableRowVariant
import ai.jessie.web.theme.HeatDayVariants
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.NumStyle
import ai.jessie.web.theme.TableNumStyle
import ai.jessie.web.theme.TableRowStyle
import ai.jessie.web.theme.TableStyle
import ai.jessie.web.theme.TodayDayVariant
import ai.jessie.web.theme.TotalTableRowVariant
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.number
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlin.js.Date
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun calendarMonth(
    calendar: WorkCalendarResponse,
    state: WorkCalendarUiState,
    actions: WorkCalendarActions,
    selectDate: (String) -> Unit,
) {
    Div(attrs = CalendarMonthTitleStyle.toAttrs()) {
        H3 { Text(calendarRangeTitle(calendar)) }
        number(calendarHours(calendar.totalMinutes), "h")
    }
    Div(attrs = CalendarScrollStyle.toAttrs()) {
        Div(attrs = CalendarMonthStyle.toAttrs { id("calendar-days") }) {
            listOf("Mo", "Di", "Mi", "Do", "Fr", "Sa", "So", "Σ").forEach {
                Span(attrs = MutedStyle.toAttrs()) { Text(it) }
            }
            val offset = (Date("${calendar.rangeStart}T00:00:00Z").getUTCDay() + 6) % 7
            val cells = List<WorkCalendarDay?>(offset) { null } + calendar.days
            cells.chunked(DAYS_PER_WEEK).forEach { week ->
                (week + List<WorkCalendarDay?>(DAYS_PER_WEEK - week.size) { null }).forEach { day ->
                    if (day == null) Span()
                    else calendarMonthDay(day, calendar, state, actions, selectDate)
                }
                Span(attrs = NumStyle.toModifier().then(CalendarSumStyle.toModifier()).toAttrs()) {
                    Text(calendarHours(week.filterNotNull().sumOf { it.totalMinutes }))
                }
            }
        }
    }
}

@Composable
private fun calendarMonthDay(
    day: WorkCalendarDay,
    calendar: WorkCalendarResponse,
    state: WorkCalendarUiState,
    actions: WorkCalendarActions,
    selectDate: (String) -> Unit,
) {
    val heat =
        when {
            day.totalMinutes == 0 -> 0
            day.totalMinutes < 240 -> 1
            day.totalMinutes < 360 -> 2
            day.totalMinutes < 480 -> 3
            day.totalMinutes <= 600 -> 4
            else -> 5
        }
    val style =
        CalendarDayControlStyle.toModifier()
            .then(
                DayStyle.toModifier(
                    HeatDayVariants[heat],
                    TodayDayVariant.takeIf { day.date == calendar.currentDate },
                )
            )
    if (day.date <= calendar.currentDate) {
        Button(
            attrs =
                style.toAttrs {
                    id("calendar-day-${day.date}")
                    attr("data-date", day.date)
                    attr("type", "button")
                    attr("aria-label", "Arbeit erfassen am ${calendarDateLabel(day.date)}")
                    attr(
                        "title",
                        "${calendarDateLabel(day.date)} · ${calendarHours(day.totalMinutes)} h",
                    )
                    if (day.date == calendar.currentDate) attr("aria-current", "date")
                    if (state.busy) attr("disabled", "")
                    onFocus { selectDate(day.date) }
                    onClick { actions.createOn(day.date) }
                }
        ) {
            Text(day.date.takeLast(2).toInt().toString())
        }
    } else
        Span(attrs = style.toAttrs { attr("data-date", day.date) }) {
            Text(day.date.takeLast(2).toInt().toString())
        }
}

// UC-009 B1: every entry is all-day; no start time is inferred from duration or order.
@Composable
internal fun calendarWeek(
    route: WorkCalendarRoute,
    calendar: WorkCalendarResponse,
    state: WorkCalendarUiState,
) {
    val projects = calendar.days.flatMap { it.entries }.map { it.projectId }.distinct()
    Div(attrs = CalendarScrollStyle.toAttrs()) {
        Ol(attrs = CalendarWeekStyle.toAttrs { id("calendar-days") }) {
            calendar.days.forEach { day ->
                Li(
                    attrs =
                        CalendarWeekDayStyle.toAttrs {
                            attr("data-date", day.date)
                            if (day.date == calendar.currentDate) attr("aria-current", "date")
                        }
                ) {
                    calendarWeekHead(day)
                    Div(attrs = CalendarAllDayStyle.toAttrs()) {
                        day.entries.forEach { entry ->
                            val (project, position) = calendarReferences(entry, state.selections)
                            A(
                                href = "/organisations/${route.organisationId}/work/${entry.id}",
                                attrs =
                                    CalendarEventStyle.toAttrs {
                                        attr(
                                            "data-project-colour",
                                            (projects.indexOf(entry.projectId) % 2).toString(),
                                        )
                                        attr(
                                            "aria-label",
                                            "${minutesLabel(entry.durationMinutes)}" +
                                                "${entry.description?.let { " · $it" }.orEmpty()} öffnen",
                                        )
                                    },
                            ) {
                                B { Text(project) }
                                Span { Text(position) }
                                Span(attrs = NumStyle.toAttrs()) {
                                    Text("${calendarHours(entry.durationMinutes)} h")
                                }
                                entry.description?.let { Span { Text(it) } }
                            }
                        }
                    }
                }
            }
        }
    }
}

// `03-Woche.png`: an empty day shows a dash; the label keeps the spoken total.
@Composable
private fun calendarWeekHead(day: WorkCalendarDay) {
    Div(attrs = CalendarWeekHeadStyle.toAttrs()) {
        H3 { Text("${weekdayLabel(day.date)} ${day.date.takeLast(2).toInt()}") }
        Span(
            attrs =
                NumStyle.toAttrs {
                    attr("aria-label", "Tagessumme: ${minutesLabel(day.totalMinutes)}")
                }
        ) {
            Text(if (day.totalMinutes == 0) "–" else "${calendarHours(day.totalMinutes)} h")
        }
    }
}

// `02-Main.png`: the day, its total and creation for that day in one head row.
@Composable
private fun calendarEntriesHead(
    day: WorkCalendarDay,
    calendar: WorkCalendarResponse,
    busy: Boolean,
    actions: WorkCalendarActions,
) {
    Div(attrs = CardHeadRowStyle.toAttrs()) {
        H2 { Text("${calendarWeekday(day.date)}, ${calendarDateLabel(day.date)}") }
        Span { number(calendarHours(day.totalMinutes), "h") }
        if (day.date <= calendar.currentDate)
            themedButton(
                "Neue Arbeit",
                { actions.createOn(day.date) },
                variant = ButtonVariant.TINT,
                size = ButtonSize.SMALL,
                disabled = busy,
            )
    }
}

@Composable
internal fun calendarEntries(
    route: WorkCalendarRoute,
    day: WorkCalendarDay,
    calendar: WorkCalendarResponse,
    state: WorkCalendarUiState,
    actions: WorkCalendarActions,
) {
    val selections = state.selections
    Div(attrs = CardStyle.toAttrs()) {
        calendarEntriesHead(day, calendar, state.busy, actions)
        Div(attrs = CalendarScrollStyle.toAttrs()) {
            Div(
                attrs =
                    TableStyle.toModifier().then(CalendarTableStyle.toModifier()).toAttrs {
                        attr("role", "table")
                        attr("aria-label", "Arbeit am ${calendarDateLabel(day.date)}")
                    }
            ) {
                Div(
                    attrs =
                        TableRowStyle.toModifier(HeadTableRowVariant)
                            .then(CalendarTableRowStyle.toModifier())
                            .toAttrs { attr("role", "row") }
                ) {
                    listOf("Projekt", "Position", "Beschreibung", "Stunden", "Aktion").forEach {
                        label ->
                        Span(attrs = { attr("role", "columnheader") }) { Text(label) }
                    }
                }
                day.entries.forEach { entry ->
                    val (project, position) = calendarReferences(entry, selections)
                    Div(
                        attrs =
                            TableRowStyle.toModifier()
                                .then(CalendarTableRowStyle.toModifier())
                                .toAttrs { attr("role", "row") }
                    ) {
                        listOf(project, position, entry.description.orEmpty()).forEach { text ->
                            Span(attrs = { attr("role", "cell") }) { Text(text) }
                        }
                        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) {
                            Text(calendarHours(entry.durationMinutes))
                        }
                        Div(attrs = { attr("role", "cell") }) {
                            linkButton(
                                "Bearbeiten",
                                "/organisations/${route.organisationId}/work/${entry.id}",
                                size = ButtonSize.SMALL,
                            )
                        }
                    }
                }
                calendarEntriesTotal(day.totalMinutes)
            }
        }
    }
}

private fun calendarReferences(
    entry: WorkResponse,
    selections: WorkSelectionsResponse?,
): Pair<String, String> {
    val project = selections?.projects?.firstOrNull { it.id == entry.projectId }
    return (project?.name ?: "nicht mehr wählbar") to
        (project?.positions?.firstOrNull { it.id == entry.positionId }?.name
            ?: "nicht mehr wählbar")
}

private const val DAYS_PER_WEEK = 7

@Composable
private fun calendarEntriesTotal(minutes: Int) {
    Div(
        attrs =
            TableRowStyle.toModifier(TotalTableRowVariant)
                .then(CalendarTableRowStyle.toModifier())
                .toAttrs { attr("role", "row") }
    ) {
        Span(attrs = { attr("role", "cell") }) { Text("Total") }
        Span(attrs = { attr("role", "cell") })
        Span(attrs = { attr("role", "cell") })
        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) {
            Text(calendarHours(minutes))
        }
        Span(attrs = { attr("role", "cell") })
    }
}
